import { handleResponse, HEADERS, SERVER_HOST } from "@api";
import { HOTEL_SUB_CATEGORY_ID } from "@components/contants";
import { TStore, TStoreMetaV2 } from "@components/types";
import { getLocation } from "@core/geolocation";
import axios from "axios";

// getLocation() waits for the browser's permission prompt; give up after a few seconds and carry on
// without a position (the list is then simply not distance-ranked).
const getLocationQuickly = (ms = 3500) =>
    Promise.race([getLocation(), new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))]);

// `coords` ranks the list around a given point (e.g. a chosen city's centre) instead of the device's
// position — used when the guest's location is unavailable or far from the area being browsed.
// `city_id` narrows the list to one city. It's an exact id match: duplicate city rows (VARANASI /
// Varanasi) have different ids, so a store saved under the twin id isn't returned for this one.
export const getNearestStores = async (category_id: number = null, pageNumber: number = 0, pageSize: number = 10, account_id: number = null, askLocation: boolean = true, coords: { latitude: number; longitude: number } | null = null, city_id: number = null) => {
    let params: any = new URLSearchParams()
    let location = coords ?? (askLocation ? await getLocationQuickly() : null);

    if (location?.latitude !== null && location?.latitude !== undefined) {
        params.append('lat', location.latitude);
    }
    if (location?.longitude !== null && location?.longitude !== undefined) {
        params.append('lon', location.longitude);
    }
    if (account_id > 0) {
        params.append('account_id', account_id);
    }
    if (category_id > 0) {
        params.append('category_id', category_id);
    }
    if (city_id > 0) {
        params.append('city_id', city_id);
    }
    // Public listings must only show published stores.
    params.append('published', 'true');
    const res = await axios.get(`${SERVER_HOST}/api/v1/stores/?${params}&skip=${pageNumber * pageSize}&limit=${pageSize}`,
        { headers: HEADERS });
    const result: TStore[] = handleResponse(res);
    return result
}

export const getLaunchedStores = async (category_id: number = null, pageNumber: number = 0, pageSize: number = 10, account_id: number | null = null) => {
    let params: any = new URLSearchParams()
    if (account_id > 0) {
        params.append('account_id', account_id);
    }
    if (category_id > 0) {
        params.append('category_id', category_id);
    }
    const res = await axios.get(`${SERVER_HOST}/api/v1/stores/newlaunch?${params}&skip=${pageNumber * pageSize}&limit=${pageSize}`,
        { headers: HEADERS });
    const result: TStore[] = handleResponse(res);
    return result
}

export async function fetchNearestStores(
    isDelivery: boolean = false,
    isPickup: boolean = false,
    categoryId: number = null,
    orderingOn: string = null,
    skip: number = 0,
    limit: number = 10,
) {
    const location = await getLocationQuickly();
    // "relevence" ranks by distance and returns nothing without a real position, so fall back to
    // popularity (and leave out the 0,0 placeholder coordinates) when the location is unknown.
    const hasLocation = Boolean(location?.latitude && location?.longitude);
    const ordering = !hasLocation && (!orderingOn || orderingOn === 'relevence') ? 'popularity' : orderingOn;
    const response = await axios.get(`${SERVER_HOST}/api/v1/stores/?${categoryId > 0 ? `category_id=${categoryId}&` : ''}${ordering ? `ordering_on=${ordering}&` : ''}${isDelivery ? `is_delivery=${isDelivery}&` : ''}${isPickup ? `is_pickup=${isPickup}&` : ''}published=true&skip=${skip}&limit=${limit}${hasLocation ? `&lat=${location.latitude}&lon=${location.longitude}` : ''}`, {
        headers: HEADERS
    });
    const data: TStore[] = handleResponse(response);
    return data;
};

export const saveStore = async (store: TStore) => {
    let axiosMethod = store.id == null ? axios.post : axios.put;
    const res = await axiosMethod(`${SERVER_HOST}/api/v1/stores/${store.id == null ? "" : store.id}`, JSON.stringify(store), {
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("token")}` }
    })
    const result: TStore = handleResponse(res)
    return result;
}

export const getStay = async (id: number) => {
    if (id > 0) {
        const res = await axios.get(`${SERVER_HOST}/api/v1/stays/${id}`, {
            headers: HEADERS
        })
        const result: TStore = handleResponse(res);
        return result;
    }
}

// Returns the stay only if it exists AND is a hotel; null otherwise (caller should 404).
export const getHotelStay = async (id: number): Promise<TStore | null> => {
    const stay = await getStay(id).catch(() => null);
    return stay && stay.sub_category_id === HOTEL_SUB_CATEGORY_ID ? stay : null;
}

export const getStore = async (id: number) => {
    if (id > 0) {
        const res = await axios.get(`${SERVER_HOST}/api/v1/stores/${id}`, {
            headers: HEADERS
        })
        const result: TStore = handleResponse(res);
        return result;
    }
}

export const getMyStores = async (pageNumber: number = 0, pageSize: number = 20, isDelivery: boolean = false, isPickup: boolean = false, categoryId: number = null, orderingOn: string = null, account_id: number = null, askLocation: boolean = false) => {
    let params: any = new URLSearchParams()
    let location = askLocation ? await getLocation() : null;
    params.append('is_delivery', isDelivery);
    params.append('is_pickup', isPickup);
    if (orderingOn?.length > 0) {
        params.append('orderingOn', orderingOn);
    }
    if (categoryId > 0) {
        params.append('category_id', categoryId?.toString());
    }
    if (account_id > 0) {
        params.append('account_id', account_id);
    }
    if (location?.latitude !== null && location?.latitude !== undefined) {
        params.append('lat', location.latitude);
    }
    if (location?.longitude !== null && location?.longitude !== undefined) {
        params.append('lon', location.longitude);
    }
    const res = await axios.get(`${SERVER_HOST}/api/v1/stores/?skip=${pageNumber * pageSize}&limit=${pageSize}&${params}`, {
        headers: HEADERS
    })
    const result: TStore[] = handleResponse(res);
    return result;
}

export const getStays = async (name: string, pageNumber: number = 0, pageSize: number = 20, cityId: number = null) => {
    let params: any = new URLSearchParams()
    params.append('sub_category_id', "29");
    if (cityId) {
        params.append('city_id', cityId.toString());
    }
    if (name) {
        params.append('name', name);
    }
    params.append("published", "true")
    const res = await axios.get(`${SERVER_HOST}/api/v1/stays/?skip=${pageNumber * pageSize}&limit=${pageSize}&${params}`, {
        headers: HEADERS
    })
    const result: TStore[] = handleResponse(res);
    return result;
}

export const getStoresByUser = async (userId: number) => {
    if (!(userId > 0)) return [];
    const res = await axios.get(`${SERVER_HOST}/api/v1/stores/?user_id=${userId}&skip=0&limit=200`, {
        headers: HEADERS
    }).catch(() => null)
    if (!res) return [];
    const result = handleResponse(res);
    if (Array.isArray(result)) return result as TStore[];
    if (Array.isArray(result?.data)) return result.data as TStore[];
    if (Array.isArray(result?.stores)) return result.stores as TStore[];
    return [];
}

// Get All Store Timing
export const getStoreImages = async (store_id: number) => {
    let axiosMethod = axios.get;
    const res = await axiosMethod(`${SERVER_HOST}/api/v1/storeimages/?store_id=${store_id}`,
        { headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("token")}` } })
    return handleResponse(res);
}

export const getStoreCharges = async (store_id: number[]) => {
    let axiosMethod = axios.get;
    let params: any = new URLSearchParams()
    for (let i = 0; i < store_id?.length; i++) {
        if (store_id[i] > 0 && !isNaN(store_id[i])) {
            params.append('store_id', store_id[i]?.toString());
        }
    }
    const res = await axiosMethod(`${SERVER_HOST}/api/v1/storecharges/?${params.toString()}`,
        { headers: { "Content-Type": "application/json" } })
    return handleResponse(res);
}

// Get Store Meta
export const getStoreMeta = async (store: TStore) => {
    let axiosMethod = axios.get;
    let params: any = new URLSearchParams()
    params.append('store_id', store?.id.toString());
    const res = await axiosMethod(`${SERVER_HOST}/api/v1/storemeta/?${params.toString()}`,
        { headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("token")}` } })
    return handleResponse(res);
}

// Create Store Meta
export const createNewStoreMeta = async (storeMeta: TStoreMetaV2) => {
    let axiosMethod = storeMeta?.id == null ? axios.post : axios.put;
    const res = await axiosMethod(`/api/v1/storemeta/${storeMeta?.id == null ? "" : storeMeta?.id}`, JSON.stringify(storeMeta))
    const result: TStoreMetaV2 = handleResponse(res)
    return result;
}

export const verifyGST = async (gst: string) => {
    let axiosMethod = axios.get
    const res = await axiosMethod(`/api/v1/stores/autofill/${gst}`)
    const result: any = handleResponse(res)
    return result;
}

// Get All Stores By IDs
export const getAllStores = async (storesIds: number[]) => {
    let axiosMethod = axios.get;
    let params: any = new URLSearchParams()
    let storeIds = storesIds;
    for (let i = 0; i < storeIds?.length; i++) {
        if (storeIds[i] > 0) {
            params.append('ids', storeIds[i].toString());
        }
    }
    params.append('skip', '0');
    params.append('limit', Math.max(storeIds?.length || 0, 10).toString());
    const res = await axiosMethod(`${SERVER_HOST}/api/v1/stores/?${params.toString()}`,
        {
            headers: {
                "Content-Type": "application/json",
                ...(typeof window !== "undefined" && localStorage.getItem("token")
                    ? { "Authorization": `Bearer ${localStorage.getItem("token")}` }
                    : {}),
            }
        })
    return handleResponse(res);
}

export const getServicability = async (storeIds: number[] = [], city_id: number = null, pageNumber: number = 0, pageSize: number = 200) => {
    let axiosMethod = axios.get;
    let params: any = new URLSearchParams()
    for (let i = 0; i < storeIds.length; i++) {
        params.append('store_id', storeIds[i]?.toString());
    }
    if (city_id != null) {
        params.append('city_id', city_id.toString());
    }
    params.append('pageNumber', pageNumber.toString());
    params.append('pageSize', pageSize.toString());
    const res = await axiosMethod(`${SERVER_HOST}/api/v1/serviceability/?${params.toString()}`,
        { headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("token")}` } })
    return handleResponse(res);
}

export const removeStoreImages = async (id: number) => {
    let axiosMethod = axios.delete;
    const res = await axiosMethod(`/api/v1/storeimages/${id}`)
    const result = handleResponse(res);
    return result;
}

export const createNewStore = async (store: TStore) => {
    let axiosMethod = axios.post
    const res = await axiosMethod(`/api/v1/stores/create`, JSON.stringify(store))
    const result: TStore = handleResponse(res)
    return result;
}


